import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import { useState, ChangeEvent } from 'react'
import PrimaryButton from '../../../../../../../global/components/buttons/primary-button/PrimaryButton'
import SecondaryButton from '../../../../../../../global/components/buttons/secondary-button/SecondaryButton'
import styles from '../styles/styles.module.css'
import useFetch from '../../../../../../../global/hooks/useFetchHook'
import { useFetchNoEffect } from '../../../../../../../global/hooks/useFetchNoUseEffectHook'
import {
  getAvailableTechsForAssignment,
  reassignTechnicianToDayPlan,
  reassignTechnicianToRoute,
} from '../../../../../../../schedule/components/api'
import CircularProgress from '@mui/material/CircularProgress'
import { ITechnician } from '../../../../../../../../../app/entities/Technician'
import isDayOptimized from '../../../../../../../global/utils/is-day-optimized'
import isDeliveryByOrderService from '../../../../../../../global/utils/is-delivery-by-order-service'
import { INSTALLATION_SERVICES } from '../../../../../../../global/constants/service'
import { IRoute } from '../../../../../../../../../app/entities/Route'
import { IOrder } from '../../../../../../../../../app/entities/Order'

interface ITechAssignmentProps {
  route: IRoute
  handleClose: Function
  date: string
  refetch: () => void
}

export default function TechAssignment({
  route,
  handleClose,
  date,
  refetch,
}: ITechAssignmentProps) {
  const [selectedTechnicianObjectId, setSelectedTechnicianObjectId] =
    useState('')

  const allOrdersOnSchedule = route.routeInstructions
    .flatMap((ri) => ri.order as IOrder | undefined)
    .filter((o): o is IOrder => !!o)

  const routeContainsDeliveries = allOrdersOnSchedule.some((order) =>
    isDeliveryByOrderService(order.orderServices),
  )

  const routeContainsInstallations = allOrdersOnSchedule.some((order) =>
    order.orderServices.some((os) => INSTALLATION_SERVICES.includes(os.label)),
  )

  const isRouteOptimized = isDayOptimized(new Date(date).valueOf())

  const { location, technician, objectId } = route

  const { data: availableTechnicians, isLoading } = useFetch<ITechnician[]>(
    getAvailableTechsForAssignment(
      location?.objectId ?? '',
      date,
      isRouteOptimized,
      routeContainsDeliveries,
      routeContainsInstallations,
    ),
  )
  const {
    handleFetchData: reassignTechToDayPlan,
    response: { isLoading: isLoadingTwo },
  } = useFetchNoEffect(
    reassignTechnicianToDayPlan(
      objectId,
      selectedTechnicianObjectId,
      technician?.objectId || '',
    ),
  )
  const {
    handleFetchData: reassignTechToRoute,
    response: { isLoading: isLoadingThree },
  } = useFetchNoEffect(
    reassignTechnicianToRoute(objectId, selectedTechnicianObjectId),
  )

  async function handleSave() {
    if (isRouteOptimized) {
      await reassignTechToRoute()
    } else {
      await reassignTechToDayPlan()
    }
    refetch()
  }

  if (isLoading) {
    return (
      <div className={styles.progressContainer}>
        <CircularProgress />
      </div>
    )
  }
  return (
    <div className={styles.inputsContainer}>
      <div className={styles.topInputsContainer}>
        Location: {location?.name}
      </div>
      <div className={styles.headerTwo}>Available Technicians</div>
      <div className={styles.topInputsContainer}>
        {!isLoading && availableTechnicians?.length ? (
          <AvailableTechniciansDropDown
            onTechChange={(val: string) => {
              setSelectedTechnicianObjectId(val)
            }}
            availableTechnicians={availableTechnicians}
          />
        ) : (
          <div className={styles.noTechs}>
            No Technicians available to reassign
          </div>
        )}
      </div>
      <div className={styles.buttonContainer}>
        <SecondaryButton
          buttonName='Cancel'
          onClick={() => handleClose()}
        />
        <PrimaryButton
          buttonName='Save'
          disabled={
            isLoadingTwo || isLoadingThree || selectedTechnicianObjectId === ''
          }
          onClick={handleSave}
        />
      </div>
    </div>
  )
}
interface IAvailableTechniciansDropDown {
  availableTechnicians: ITechnician[]
  onTechChange: Function
}
function AvailableTechniciansDropDown(props: IAvailableTechniciansDropDown) {
  const { availableTechnicians, onTechChange } = props
  const [value, setValue] = useState('')

  function onTechChangeWrapper(
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) {
    const {
      target: { value: newValue },
    } = event
    setValue(newValue)
    onTechChange && onTechChange(newValue)
  }
  const menuItems = availableTechnicians.map(
    (technician: ITechnician, index: number) => {
      const { name, objectId } = technician
      return (
        <MenuItem
          key={index}
          value={objectId}
        >
          {name}
        </MenuItem>
      )
    },
  )
  return (
    <TextField
      fullWidth
      label='Technician Name'
      select
      value={value}
      onChange={onTechChangeWrapper}
      SelectProps={{
        MenuProps: {
          PaperProps: {
            style: {
              maxHeight: 500,
              width: 200,
            },
          },
        },
      }}
    >
      {menuItems}
    </TextField>
  )
}
